import { clsx } from 'clsx'
import {
  Ban,
  Blocks,
  Check,
  Compass,
  HelpCircle,
  Sticker,
  MonitorPlay,
  MousePointerClick,
  NotebookPen,
  Play,
  Gamepad2,
  type LucideIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ComponentProgressDetailView } from '@/types/coaching'
import { formatDuration } from '@/utils/time'
import { openGameMathPage } from './gameMathNavigation'
import { componentNeedsTeacherGrading } from './lessonOperationStatus'

interface Props {
  component: ComponentProgressDetailView
  index: number
  selected: boolean
  gameKeys?: string[]
  onSelect: (componentProgressId: number) => void
  collapsed?: boolean
}

const TYPE_ICON: Record<
  ComponentProgressDetailView['type'],
  { Icon: LucideIcon; className: string }
> = {
  video: { Icon: MonitorPlay, className: 'text-sky-600' },
  quiz: { Icon: HelpCircle, className: 'text-red-500' },
  guide: { Icon: Compass, className: 'text-blue-500' },
  toon: { Icon: Sticker, className: 'text-pink-500' },
  assignment: { Icon: NotebookPen, className: 'text-emerald-600' },
  interactive: { Icon: MousePointerClick, className: 'text-violet-500' },
  buildup: { Icon: Blocks, className: 'text-amber-500' },
  warmup: { Icon: Compass, className: 'text-yellow-500' },
}

export function UnitListItem({ component, index, selected, gameKeys = [], onSelect, collapsed = false }: Props) {
  const { t } = useTranslation('coaching')
  const isSkipped = component.status === 'skipped'
  // 건너뛴 생각열기(learner_choice/content_missing)는 사유·안내를 상세에서 보여주므로 선택 가능하다.
  const isSelectable =
    component.status === 'completed' ||
    component.status === 'in_progress' ||
    (isSkipped && component.skippedReason != null && component.skippedReason !== 'previous_null')
  const gameKey = component.type === 'interactive' && component.interactive?.kind === 'game' ? gameKeys[0] : undefined
  const { Icon, className: iconClassName } = TYPE_ICON[component.type]
  const metaText = getUnitMetaText(component, t)
  const needsGrading = componentNeedsTeacherGrading(component)
  const label = component.label || t(`component.type.${component.type}`)
  const statusLabel = t(`unit.list.status.${component.status}`)

  if (collapsed) {
    return (
      <button
        type="button"
        aria-current={selected ? 'step' : undefined}
        aria-label={`${index + 1}. ${label}, ${statusLabel}${needsGrading ? `, ${t('unit.list.needsGrading')}` : ''}`}
        title={`${index + 1}. ${label}`}
        data-compact="true"
        data-needs-grading={needsGrading ? 'true' : undefined}
        disabled={!isSelectable}
        onClick={() => onSelect(component.componentProgressId)}
        className={clsx(
          'relative flex size-10 items-center justify-center rounded-lg border transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
          selected ? 'border-primary bg-accent text-accent-foreground' : 'border-transparent hover:bg-muted',
          !isSelectable && 'cursor-not-allowed opacity-50',
        )}
      >
        <span className="absolute left-1 top-0.5 text-[9px] font-semibold tabular-nums text-muted-foreground">
          {index + 1}
        </span>
        <Icon className={clsx('size-4', iconClassName)} aria-hidden />
        {needsGrading ? (
          <span
            className="absolute right-1 top-1 size-2 rounded-full bg-destructive ring-2 ring-background"
            aria-hidden
          />
        ) : null}
      </button>
    )
  }

  return (
    <div
      className={clsx(
        'flex items-stretch gap-1 rounded-lg border transition',
        selected ? 'border-primary-600 bg-primary-50' : 'border-transparent',
        isSelectable && !selected && 'hover:bg-gray-50',
        !isSelectable && 'opacity-60',
      )}
    >
      <button
        type="button"
        aria-current={selected ? 'step' : undefined}
        disabled={!isSelectable}
        onClick={() => onSelect(component.componentProgressId)}
        className={clsx(
          'flex min-w-0 flex-1 items-center gap-3 px-3 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1',
          isSelectable ? 'cursor-pointer' : 'cursor-not-allowed',
        )}
      >
        <span className="text-sm text-gray-400 w-5 text-right tabular-nums">
          {index + 1}
        </span>
        <Icon
          className={clsx('w-5 h-5 shrink-0', iconClassName)}
          aria-hidden
        />
        <span className="flex-1 min-w-0">
          <span className="flex min-w-0 flex-wrap items-center gap-1.5">
            <span className="min-w-0 truncate text-sm font-medium text-gray-900">
              {label}
            </span>
            {needsGrading ? (
              <span className="inline-flex shrink-0 rounded-full border border-red-200 bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">
                {t('unit.list.needsGrading')}
              </span>
            ) : null}
          </span>
          {isSkipped && (
            <span className="block text-[11px] text-gray-500 mt-0.5">
              {component.skippedReason === 'learner_choice' || component.skippedReason === 'content_missing'
                ? t(`unit.skipReason.${component.skippedReason}`)
                : t('component.skippedReason')}
            </span>
          )}
          {metaText && (
            <span className="mt-1 block text-sm font-bold text-gray-700">
              {metaText}
            </span>
          )}
        </span>
        <StatusBadge status={component.status} t={t} />
      </button>
      {gameKey && (
        <button
          type="button"
          onClick={() => openGameMathPage(gameKey)}
          className="m-2 inline-flex shrink-0 items-center gap-1 rounded border border-cyan-200 bg-cyan-50 px-2 text-[11px] font-semibold text-cyan-700 hover:bg-cyan-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-1"
        >
          <Gamepad2 size={12} /> {t('library.gameMathTag')}
        </button>
      )}
    </div>
  )
}

function getUnitMetaText(
  component: ComponentProgressDetailView,
  t: ReturnType<typeof useTranslation>['t'],
): string | null {
  if (component.type === 'video' && component.elapsedSeconds != null && component.elapsedSeconds > 0) {
    return t('unit.list.videoDuration', { duration: formatDuration(component.elapsedSeconds) })
  }

  if (component.type === 'quiz' && component.quiz) {
    return t('unit.list.quizStats', {
      questions: component.quiz.questionCount,
      correct: component.quiz.correctCount,
      rate: formatRate(component.quiz.correctRate),
    })
  }

  return null
}

function formatRate(rate: number): string {
  return Number.isInteger(rate) ? String(rate) : rate.toFixed(1)
}

function StatusBadge({
  status,
  t,
}: {
  status: ComponentProgressDetailView['status']
  t: ReturnType<typeof useTranslation>['t']
}) {
  const label = t(`unit.list.status.${status}`)
  const baseProps = {
    'aria-label': label,
    className: 'w-4 h-4 shrink-0',
  }
  const icon = (() => {
    switch (status) {
      case 'completed':
        return <Check {...baseProps} className="w-3.5 h-3.5 shrink-0 text-green-600" strokeWidth={3} />
      case 'in_progress':
      case 'not_started':
        return (
          <Play
            {...baseProps}
            className="w-3.5 h-3.5 shrink-0 text-blue-500"
            fill="currentColor"
          />
        )
      case 'skipped':
        return <Ban {...baseProps} className="w-3.5 h-3.5 shrink-0 text-gray-400" />
    }
  })()

  switch (status) {
    case 'completed':
      return (
        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-green-50 px-2 py-1 text-[11px] font-semibold text-green-700">
          {icon}
          {label}
        </span>
      )
    case 'in_progress':
      return (
        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-blue-50 px-2 py-1 text-[11px] font-semibold text-blue-700">
          {icon}
          {label}
        </span>
      )
    case 'not_started':
      return (
        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-gray-100 px-2 py-1 text-[11px] font-semibold text-gray-600">
          {icon}
          {label}
        </span>
      )
    case 'skipped':
      return (
        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-gray-100 px-2 py-1 text-[11px] font-semibold text-gray-500">
          {icon}
          {label}
        </span>
      )
  }
}
